import { useEffect, useLayoutEffect, useMemo } from "react";
import cn from "classnames";
import { AppInputParsedData } from "./App.types";
import { Panel } from "widgets/Panel";
import { AirDemoModeBanner } from "features/AirDemoModeBanner";
// import { AirPayments } from "features/AirPayments";
import { useCheckApiAccessMutation } from "features/CheckIntegrationAccess";
import { useLang } from "features/I18n";
import { openedCardStore } from "features/OpenedCard";
import { useSearchTypeParamHandler } from "features/QueryParamsManager";
import { useInitialSearch } from "features/QueryParamsManager";
// import { SeaPayments } from "features/SeaPayments";
import { useSeaSearch } from "features/Search";
import { useAirSearch, useParcelSearch, useRailSearch, useRoadSearch } from "features/Search";
import { UpgradeButton } from "features/shared/UpgradeButton";
import { mapThemeStore, mapUiFromTheme, ThemeSwitcher } from "features/ThemeSwitcher";
import { ToolsTabs } from "features/ToolsTabs/ui/ToolsTabs";
import { useAirCarriersQuery } from "entities/AirCarriers";
import { appStore } from "entities/App";
import { authStore, useCurrentUserQuery } from "entities/Auth";
import { useSealinesQuery } from "entities/Sealines";
import { searchFilterStore } from "entities/SearchFilter";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { APP_CONTAINER_NODE_CLASSNAME } from "shared/constants/common.constants";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { useTranslation } from "shared/localization/localization";
import { AppRenderErrorModal } from "shared/ui/AppRenderErrorModal";
import { MobileToggle } from "shared/ui/MobileToggle";
import { Preloader } from "shared/ui/Preloader";
import { AppDOM } from "shared/utils/app.utils";
import { isAppIntegration } from "shared/utils/url.utils";
import { MapWrapper, PanelWrapper } from "./layouts";
import { Modals } from "./Modals";
import { RootProviders } from "./RootProviders";
import "styles/index.scss";
import styles from "./App.module.scss";

export type AppProps = {
  inputData: AppInputParsedData;
};

const AppBase: RFC<{
  inputData: AppInputParsedData;
}> = ({ inputData: { hideCardSearch, hideCardShippingLine } }) => {
  const dispatch = useAppDispatch();
  const { isSearchTypeParamInitialized } = useSearchTypeParamHandler();

  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const { isAuthResolved } = useCurrentUserQuery();

  useEffect(() => {
    dispatch(appStore.actions.setIsCardSearchVisibleAppConfig(!hideCardSearch));
    dispatch(appStore.actions.setIsCardShippingLineVisibleAppConfig(!hideCardShippingLine));
  }, []);

  const { seaSearchRequest, getSeaCards, isSeaSearchLoading } = useSeaSearch({
    isAuthenticated: isAuthenticated,
  });
  const { airSearchRequest, getAirCards, isAirSearchLoading } = useAirSearch({
    isAuthenticated: isAuthenticated,
  });
  const { railSearchRequest, isRailSearchLoading } = useRailSearch({
    isAuthenticated: isAuthenticated,
  });
  const { roadSearchRequest, isRoadSearchLoading } = useRoadSearch({
    isAuthenticated: isAuthenticated,
  });
  const { parcelSearchRequest, isParcelSearchLoading } = useParcelSearch({
    isAuthenticated: isAuthenticated,
  });

  useInitialSearch({
    seaSearchController: {
      searchRequest: seaSearchRequest,
      getCards: getSeaCards,
      isSearchLoading: isSeaSearchLoading,
    },
    airSearchController: {
      searchRequest: airSearchRequest,
      getCards: getAirCards,
      isSearchLoading: isAirSearchLoading,
    },
    railSearchController: {
      searchRequest: railSearchRequest,
      isSearchLoading: isRailSearchLoading,
    },
    roadSearchController: {
      searchRequest: roadSearchRequest,
      isSearchLoading: isRoadSearchLoading,
    },
    parcelSearchController: {
      searchRequest: parcelSearchRequest,
      isSearchLoading: isParcelSearchLoading,
    },
    openedCardAction: openedCardStore.actions.setOpenedCardAction,
    isSearchTypeParamInitialized,
    isAuthResolved,
  });

  return null;
};

const AppBaseWrapper: RFC<AppProps> = ({
  inputData: { platform, lang, /* tokenUrl, */ allowedShipmentTypes },
  children,
}) => {
  const dispatch = useAppDispatch();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const isShipmentTypeConfirmed = useAppSelector(searchFilterStore.selectors.isShipmentTypeConfirmed);
  const isGlobalTracking = useAppSelector(searchFilterStore.selectors.isGlobalTracking);
  const openedCard = useAppSelector(openedCardStore.selectors.openedCard);
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const mobileToggleVariant =
    isGlobalTracking && !isShipmentTypeConfirmed ? "global" : ShipmentTypeChecker.isAir(shipment) ? "air" : "sea";

  useLang({ lang });
  useSealinesQuery({ requestOnMount: !ShipmentTypeChecker.isAir(shipment) });
  useAirCarriersQuery({ requestOnMount: true });

  const { isCheckAccessSuccess } = useCheckApiAccessMutation({
    checkOnMount: isAppIntegration,
    platformId: platform,
  });

  // Who is looking at the page. Embedded integrations authenticate by their
  // own API access check instead, so the session lookup only runs on our site.
  const { isAuthResolved } = useCurrentUserQuery();

  const isPanelVisible = useAppSelector(appStore.selectors.isPanelVisible);
  const mapTheme = useAppSelector(mapThemeStore.selectors.style);
  const uiTheme = mapUiFromTheme(mapTheme);

  useLayoutEffect(() => {
    const container = AppDOM.appContainer ?? document.querySelector<HTMLDivElement>(`.${APP_CONTAINER_NODE_CLASSNAME}`);
    if (!container) return;
    container.setAttribute("data-theme", uiTheme);
  }, [uiTheme]);

  const appHasAccess = useMemo(() => {
    if (isAppIntegration) {
      return isCheckAccessSuccess;
    }
    // Guests get the panel too - it just holds no saved shipments. Waiting for
    // the session lookup keeps the panel from rendering the guest view first
    // and swapping it once the answer arrives.
    return isAuthResolved;
  }, [isAppIntegration, shipment, isCheckAccessSuccess, isAuthResolved]);

  const isToolsTabsVisibleInPanel = useMemo(() => {
    return appHasAccess && isAuthenticated;
  }, [appHasAccess, isAuthenticated]);

  useEffect(() => {
    if (!platform) return;
    dispatch(authStore.actions.setPlatformAction(platform));
  }, [platform]);

  useEffect(() => {
    dispatch(searchFilterStore.actions.setAllowedShipmentTypes(allowedShipmentTypes));
    if (allowedShipmentTypes.length === 1) {
      dispatch(searchFilterStore.actions.setShippingType(allowedShipmentTypes[0]));
    }
  }, [allowedShipmentTypes]);

  const Payments = useMemo(() => {
    return null;
    // switch (shipment) {
    //   case ShipmentType.SEA:
    //     return <SeaPayments />;
    //   case ShipmentType.AIR:
    //     return <AirPayments />;
    // }
  }, [shipment]);

  const isUpgradeButtonVisible = useMemo(() => {
    return !isAuthenticated && !isAppIntegration;
  }, [isAuthenticated, isAppIntegration]);

  // Logged-in users switch via ToolsTabs. The bottom Details/Map bar is for guests.
  const shouldShowMobileToggle = isAuthResolved && !isAuthenticated;

  return (
    <div className={styles.totalTrackingApp} data-version={__APP_VERSION__} data-theme={uiTheme}>
      {isToolsTabsVisibleInPanel && <ToolsTabs toolbarStart={<ThemeSwitcher />}>{Payments}</ToolsTabs>}
      {!isToolsTabsVisibleInPanel && (
        <div className={styles.mapTools}>
          {isUpgradeButtonVisible && (
            <div className={styles.upgradeBtnWrapper}>
              <UpgradeButton />
            </div>
          )}
          <div className={styles.themeSwitcher}>
            <ThemeSwitcher />
          </div>
        </div>
      )}
      <PanelWrapper
        className={cn(
          { [styles.hiddenDetails]: !isPanelVisible },
          { [styles.isTabsVisible]: isToolsTabsVisibleInPanel },
          { [styles.isUpgradeButtonVisible]: isUpgradeButtonVisible }
        )}
      >
        {appHasAccess ? <Panel /> : <Preloader />}
      </PanelWrapper>
      <MapWrapper />
      {shouldShowMobileToggle && (
        <MobileToggle
          isPanelVisible={isPanelVisible}
          variant={mobileToggleVariant}
          onSwitch={(isPanelVisible) => dispatch(appStore.actions.setIsPanelVisible(isPanelVisible))}
        />
      )}
      <AirDemoModeBanner openedCard={openedCard} />
      {children}
    </div>
  );
};

export const App: RFC<AppProps> = ({ inputData }) => {
  const { t } = useTranslation();

  if (!inputData.platform) {
    return <AppRenderErrorModal message={t("Platform is not defined")} />;
  }

  return (
    <RootProviders>
      <AppBaseWrapper inputData={inputData}>
        <AppBase inputData={inputData} />
        <Modals authData={inputData.authData} platform={inputData.platform} />
      </AppBaseWrapper>
    </RootProviders>
  );
};
